import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import TableContainer from '@mui/material/TableContainer';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import AddIcon from '@mui/icons-material/Add';
import { api } from '../../api/api';
import toast from 'react-hot-toast';

const SIZE_KEYS = ['hot_12', 'hot_16', 'ice_16', 'ice_20'];
const SIZE_LABELS = { hot_12: 'HOT 12oz', hot_16: 'HOT 16oz', ice_16: 'ICE 16oz', ice_20: 'ICE 20oz' };

function MenuForm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [storeId, setStoreId] = useState('');
  const [prices, setPrices] = useState({}); // { menuCode: { hot_s: 3.7, ... } }
  const [customItems, setCustomItems] = useState([]); // [{ name, prices: {}, calories: {} }]
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await api.get(`/menu/form-data/${token}`);
        setData(res.data);
        if (res.data.stores?.length) setStoreId(res.data.stores[0]._id);
      } catch (e) {
        toast.error('유효하지 않거나 만료된 링크입니다.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const updatePrice = (menuCode, sizeKey, value) => {
    setPrices(prev => ({
      ...prev,
      [menuCode]: { ...(prev[menuCode] || {}), [sizeKey]: value === '' ? undefined : Number(value) },
    }));
  };

  const addCustomItem = () => {
    setCustomItems(prev => [...prev, { name: '', prices: {}, calories: {} }]);
  };
  const updateCustomItem = (idx, field, key, value) => {
    setCustomItems(prev => {
      const next = [...prev];
      if (!next[idx]) return prev;
      if (field === 'name') next[idx] = { ...next[idx], name: value };
      else next[idx] = { ...next[idx], [field]: { ...next[idx][field], [key]: value === '' ? undefined : Number(value) } };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !data) return;
    if (!storeId && data.stores?.length) {
      toast.error('점포를 선택하세요.');
      return;
    }
    setSaving(true);
    try {
      const items = [];
      (data.categories || []).forEach(cat => {
        (data.itemsByCategory?.[cat.id] || []).forEach(item => {
          const p = prices[item.code];
          if (p && Object.keys(p).some(k => p[k] != null)) {
            items.push({ menuCode: item.code, menuName: item.name, prices: p });
          }
        });
      });
      const custom = customItems.filter(c => c.name?.trim()).map(c => ({
        name: c.name.trim(),
        prices: c.prices || {},
        calories: Object.keys(c.calories || {}).length ? c.calories : undefined,
      }));
      await api.post('/menu/customer-form', { token, storeId, items, customItems: custom });
      setSaved(true);
      toast.success('저장되었습니다.');
    } catch (err) {
      toast.error(err?.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
        <Typography>로딩 중...</Typography>
      </Box>
    );
  }
  if (!data) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}><Typography color="error">링크를 불러올 수 없습니다.</Typography></Paper>
      </Box>
    );
  }
  if (saved) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
        <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center', borderRadius: 3 }}>
          <RestaurantMenuIcon sx={{ color: '#551FFF', fontSize: 48, mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#551FFF', mb: 1 }}>메뉴 가격이 저장되었습니다.</Typography>
          <Typography variant="body2" color="text.secondary">관리자 확인 후 반영됩니다.</Typography>
        </Paper>
      </Box>
    );
  }

  const stores = data.stores || [];
  if (stores.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
        <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary">등록된 점포가 없습니다. 먼저 점포 정보 입력 링크로 점포를 등록해 주세요.</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', p: 2, background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
      <Paper sx={{ maxWidth: 900, mx: 'auto', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(85,31,255,0.12)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <RestaurantMenuIcon sx={{ color: '#551FFF', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#551FFF' }}>메뉴보드 가격 입력</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>사이즈별 가격을 입력하세요. 칼로리는 자동으로 표시됩니다.</Typography>

        <form onSubmit={handleSubmit}>
          {stores.length > 1 && (
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>점포</InputLabel>
              <Select label="점포" value={storeId} onChange={e => setStoreId(e.target.value)}>
                {stores.map(s => <MenuItem key={s._id} value={s._id}>{s.tljNo || s.name || s._id}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {(data.categories || []).map(cat => (
            <Box key={cat.id} sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#551FFF', mb: 2 }}>{cat.label}</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#f5f3ff' }}>
                      <TableCell sx={{ fontWeight: 700 }}>제품명</TableCell>
                      {SIZE_KEYS.map(k => (
                        <TableCell key={k} sx={{ fontWeight: 600 }}>{SIZE_LABELS[k]} 가격</TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 600 }}>칼로리</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.itemsByCategory?.[cat.id] || []).map(item => (
                      <TableRow key={item._id}>
                        <TableCell>{item.name} {item.nameEn && <span style={{ color: '#888', fontSize: 12 }}>({item.nameEn})</span>}</TableCell>
                        {SIZE_KEYS.map(k => (
                          <TableCell key={k}>
                            <TextField
                              type="number"
                              size="small"
                              inputProps={{ step: 0.01, min: 0 }}
                              value={prices[item.code]?.[k] ?? ''}
                              onChange={e => updatePrice(item.code, k, e.target.value)}
                              sx={{ width: 72 }}
                            />
                          </TableCell>
                        ))}
                        <TableCell sx={{ fontSize: 12, color: '#666' }}>
                          {SIZE_KEYS.map(k => (item.calories || {})[k] != null && `${SIZE_LABELS[k]}: ${item.calories[k]}kcal`).filter(Boolean).join(' · ') || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#551FFF', mb: 2 }}>추가 제품 (메뉴얼 입력)</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addCustomItem} sx={{ mb: 1 }}>제품 추가</Button>
            {customItems.map((row, idx) => (
              <TableContainer key={idx} component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: 140 }}><TextField size="small" fullWidth placeholder="제품명" value={row.name} onChange={e => updateCustomItem(idx, 'name', null, e.target.value)} /></TableCell>
                      {SIZE_KEYS.map(k => (
                        <TableCell key={k}>
                          <TextField type="number" size="small" placeholder={SIZE_LABELS[k]} inputProps={{ step: 0.01 }} value={row.prices?.[k] ?? ''} onChange={e => updateCustomItem(idx, 'prices', k, e.target.value)} sx={{ width: 72 }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ))}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
            <Button type="submit" variant="contained" disabled={saving} sx={{ background: '#551FFF', borderRadius: 2 }}>{saving ? '저장 중...' : '저장'}</Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

export default MenuForm;
